import { TWPERMISSIONS } from 'dataSource/auth/types'
import { internalPageRoutes } from 'constants/routing'

export enum SessionTabLabels {
  UPCOMING = 'Upcoming',
  PAST = 'Past',
  CANCELLED = 'Canceled',
}

export enum SessionTabValues {
  UPCOMING = 'upcoming',
  PAST = 'past',
  CANCELLED = 'canceled',
}

export const SESSION_TABS: SelectItem[] = [
  {
    label: SessionTabLabels.UPCOMING,
    value: SessionTabValues.UPCOMING,
  },
  {
    label: SessionTabLabels.PAST,
    value: SessionTabValues.PAST,
  },
  {
    label: SessionTabLabels.CANCELLED,
    value: SessionTabValues.CANCELLED,
  },
]

export enum AccountTabLabels {
  SETTINGS = 'Settings',
  PATIENT_DETAILS = 'Patient details',
  DOCUMENTS = 'Documents',
}

export enum AccountTabValues {
  SETTINGS = 'settings',
  PATIENT_DETAILS = 'patient_details',
  DOCUMENTS = 'documents',
}

export const ACCOUNT_TABS: SelectItem[] = [
  {
    label: AccountTabLabels.SETTINGS,
    value: AccountTabValues.SETTINGS,
  },
  {
    label: AccountTabLabels.PATIENT_DETAILS,
    value: AccountTabValues.PATIENT_DETAILS,
  },
  {
    label: AccountTabLabels.DOCUMENTS,
    value: AccountTabValues.DOCUMENTS,
  },
]

export const dashboardHeaderLinkText = {
  DASHBOARD: 'Dashboard',
  INSURANCE_VERIFICATION: 'Verify Insurance',
  SESSIONS: 'Sessions',
  BILLING: 'Billing',
  INSURANCE_CARDS: 'Insurance Cards(Only For Dev)',
  FIRESIDE_COURSES: 'Fireside courses',
  ACCOUNT: 'Account',
  BLOG: 'Blog',
  MENTAL_HEALTH_TOPICS: 'Mental health topics',
  TESTIMONIALS: 'Testimonials',
  PROFESSIONAL_COUNSELING: 'What is Thriveworks',
  BOOK_SESSION: 'Find a provider',
  THRIVECARE_NENA: 'Emergency Communications Locator (NENA)',
  INTERNAL_TOOLS: 'Internal Tools',
}

// Staff roles allowed to open the Internal Tools hub. Single source of truth
// shared by the hub's own access guard and every nav link that points to it,
// so the link only ever shows for people who can actually use the page.
export const INTERNAL_TOOLS_PERMISSIONS: TWPERMISSIONS[] = [
  TWPERMISSIONS.SOFTWARE_DEVS,
  TWPERMISSIONS.CLINICAL_ADMIN,
  TWPERMISSIONS.DEVOPS_ADMIN,
  TWPERMISSIONS.THRIVECARE_ADMIN,
  TWPERMISSIONS.RCM,
  TWPERMISSIONS.MARKETING_ADMIN,
]

// Who may open each /internal tool. Tools not listed default to
// INTERNAL_DEFAULT_PERMISSIONS. Keep each entry in step with its API guard.
export const INTERNAL_DEFAULT_PERMISSIONS: TWPERMISSIONS[] = [
  TWPERMISSIONS.MARKETING_ADMIN,
  TWPERMISSIONS.SOFTWARE_DEVS,
  TWPERMISSIONS.DEVOPS_ADMIN,
  TWPERMISSIONS.PRODUCT_TEAM,
]

export const INTERNAL_ROUTE_PERMISSIONS: Record<string, TWPERMISSIONS[]> = {
  [internalPageRoutes.SLOT_EXPLAINER]: [
    TWPERMISSIONS.DEVOPS_ADMIN,
    TWPERMISSIONS.SOFTWARE_DEVS,
  ],
}

export const getInternalRoutePermissions = (
  pathname: string
): TWPERMISSIONS[] => {
  const route = Object.keys(INTERNAL_ROUTE_PERMISSIONS).find(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
  return route
    ? INTERNAL_ROUTE_PERMISSIONS[route]
    : INTERNAL_DEFAULT_PERMISSIONS
}

export const canAccessInternalTools = (permissions: TWPERMISSIONS[]): boolean =>
  permissions.some((permission) =>
    INTERNAL_TOOLS_PERMISSIONS.includes(permission)
  )

export const navDropdownLinks = {
  PROFESSIONAL_COUNSELING: '/professional-counseling',
  BLOG: '/help-with/mental-health-topics/',
}

export enum BillingTableColumnHeaders {
  DATE = 'Date',
  DESCRIPTION = 'Description',
  PROVIDER = 'Provider',
  CHARGES = 'Charges',
  ADJUSTMENTS = 'Adjustments',
  PAYMENTS = 'Payments',
  MY_BALANCE = 'My balance',
  EXPAND_ROW = 'Expand',
}

export enum TransactionHistoryTableColumnHeaders {
  DATE = 'Date',
  DESCRIPTION = 'Description',
  PAYMENTS = 'Payments',
  TOTAL_PAID = 'Total paid',
  RECEIPT = 'Receipt',
}

export enum BillingTransactionStatus {
  //Charges
  SESSION_CHARGE = 'Session charge',
  INSURANCE_PENDING = 'Insurance Pending',
  PATIENT_PENDING = 'Patient Pending',
  //Adjustments
  INSURANCE_ADJUSTMENT = 'Insurance Adjustment',
  CLIENT_ADJUSTMENT = 'Client Adjustment',
  //Payments
  INSURANCE_PAYMENT = 'Insurance Payment',
  CLIENT_PAYMENT = 'Client Payment',
  //Patient balance
  PATIENT_BALANCE = 'Patient balance',
  //Insurance balance
  INSURANCE_BALANCE = 'Insurance balance',
  //Total balalance
  MY_BALANCE = 'My balance',
}

export enum BillingTransactionType {
  CHARGE = 'Charge',
  ADJUSTMENT = 'Adjustment',
  PAYMENT = 'Payment',
  BALANCE = 'Total Balance',
}

export type InsuranceSuggestionCode =
  | InsuranceSuggestionCodeEnum
  | InsuranceSuggestionCodeDetail

export enum InsuranceSuggestionCodeEnum {
  GENERAL_DEFAULT = 'GENERAL_DEFAULT',
  GENERAL_EXTERNAL_API_ERROR = 'GENERAL_EXTERNAL_API_ERROR',
  GENERAL_INTERNAL_API_ERROR = 'GENERAL_INTERNAL_API_ERROR',
  GENERAL_INTERNAL_DATA_ERROR = 'GENERAL_INTERNAL_DATA_ERROR',

  INLINE_PATIENT_NAME_UNRECOGNIZED = 'INLINE_PATIENT_NAME_UNRECOGNIZED',
  INLINE_DOB_UNRECOGNIZED = 'INLINE_DOB_UNRECOGNIZED',
  INLINE_MEMBER_ID_UNRECOGNIZED = 'INLINE_MEMBER_ID_UNRECOGNIZED',

  INLINE_FIRST_NAME_SUGGESTION = 'INLINE_FIRST_NAME_SUGGESTION',
  INLINE_LAST_NAME_SUGGESTION = 'INLINE_LAST_NAME_SUGGESTION',
  INLINE_DOB_SUGGESTION = 'INLINE_DOB_SUGGESTION',
  INLINE_MEMBER_ID_SUGGESTION = 'INLINE_MEMBER_ID_SUGGESTION',
}

export type InsuranceSuggestionCodeDetail = {
  type: InsuranceSuggestionCodeEnum
  value?: string
  raw: string
}

export const isSuggestionDetail = (
  suggestionCode: InsuranceSuggestionCode
): suggestionCode is InsuranceSuggestionCodeDetail =>
  suggestionCode &&
  (suggestionCode as InsuranceSuggestionCodeDetail)?.type !== undefined

export const getSuggestionType = (
  code: InsuranceSuggestionCode
): InsuranceSuggestionCodeEnum => (isSuggestionDetail(code) ? code.type : code)

export const getSuggestionValue = (code: InsuranceSuggestionCode): string =>
  isSuggestionDetail(code) && code?.value ? code.value : null

export enum InsuranceVerificationField {
  MEMBER_ID = 'memberId',
  PATIENT_DOB = 'dateOfBirth',
  PATIENT_FULL_NAME = 'firstnameOrLastname',
}

type InsuranceSuggestedRevision = {
  level: 'general' | 'inline'
  field_name?: InsuranceVerificationField
  message: string | ((val: string) => string)
}

export const InsuranceSuggestionCodeMapping: Record<
  InsuranceSuggestionCodeEnum,
  InsuranceSuggestedRevision
> = {
  [InsuranceSuggestionCodeEnum.GENERAL_EXTERNAL_API_ERROR]: {
    level: 'general',
    message:
      'We did not receive a response from the insurance provider. This may be due to system updates. We are aware and working with our partners to resolve it.',
  },
  [InsuranceSuggestionCodeEnum.GENERAL_INTERNAL_API_ERROR]: {
    level: 'general',
    message:
      'We did not receive a response from the insurance provider. This may be due to system updates. We are aware and working with our partners to resolve it.',
  },
  [InsuranceSuggestionCodeEnum.GENERAL_INTERNAL_DATA_ERROR]: {
    level: 'general',
    message:
      'We did not receive a response from the insurance provider. This may be due to system updates. We are aware and working with our partners to resolve it.',
  },
  [InsuranceSuggestionCodeEnum.INLINE_DOB_UNRECOGNIZED]: {
    level: 'inline',
    field_name: InsuranceVerificationField.PATIENT_DOB,
    message:
      "Verify the client's date of birth. It should be entered using the MM/DD/YYYY format.",
  },
  [InsuranceSuggestionCodeEnum.INLINE_MEMBER_ID_UNRECOGNIZED]: {
    level: 'inline',
    field_name: InsuranceVerificationField.MEMBER_ID,
    message:
      "Verify the client's member ID. Member IDs may be different for individuals on the same policy.",
  },
  [InsuranceSuggestionCodeEnum.INLINE_PATIENT_NAME_UNRECOGNIZED]: {
    level: 'inline',
    field_name: InsuranceVerificationField.PATIENT_FULL_NAME,
    message:
      "Verify the client's name is spelled correctly and is exactly what is on their insurance card.",
  },
  [InsuranceSuggestionCodeEnum.INLINE_FIRST_NAME_SUGGESTION]: {
    level: 'inline',
    field_name: InsuranceVerificationField.PATIENT_FULL_NAME,
    message: (val) => `Try "${val}" as the First Name.`,
  },
  [InsuranceSuggestionCodeEnum.INLINE_LAST_NAME_SUGGESTION]: {
    level: 'inline',
    field_name: InsuranceVerificationField.PATIENT_FULL_NAME,
    message: (val) => `Try "${val}" as the Last Name.`,
  },
  [InsuranceSuggestionCodeEnum.INLINE_DOB_SUGGESTION]: {
    level: 'inline',
    field_name: InsuranceVerificationField.PATIENT_DOB,
    message: (val) => `Try "${val}" as the Date of Birth.`,
  },
  [InsuranceSuggestionCodeEnum.INLINE_MEMBER_ID_SUGGESTION]: {
    level: 'inline',
    field_name: InsuranceVerificationField.MEMBER_ID,
    message: (val) => `Try "${val}" as Member ID.`,
  },
  [InsuranceSuggestionCodeEnum.GENERAL_DEFAULT]: {
    level: 'general',
    message:
      "Verify the client's information and try again. Ask if there are any other company names or logos on their card that may indicate a different carrier.",
  },
}

export const billingHistoryTableMapping = {
  billing_history: {
    label: 'Billing history',
    value: 'billing_history',
    notes:
      'Expand each session to see a detailed breakdown of charges and payments.',
  },
  transaction_history: {
    label: 'Transaction history',
    value: 'transaction_history',
    notes:
      'View all payments, which sessions they applied to, and access your PDF receipts.',
  },
}

export const billingHistoryTab: SelectItem = {
  label: billingHistoryTableMapping.billing_history.label,
  value: billingHistoryTableMapping.billing_history.value,
}

export const transactionHistoryTab: SelectItem = {
  label: billingHistoryTableMapping.transaction_history.label,
  value: billingHistoryTableMapping.transaction_history.value,
}

export const allTabs: SelectItem[] = [billingHistoryTab, transactionHistoryTab]

export const maxBillingStatementApiCallCounter = 30
export const maxSessionBillingStatementApiCallCounter = 15

export const delayBillingStatementApiCall = 3000

export enum SearchParam {
  ACCOUNT_NUMBER = 'Account Number',
  INTERNAL_ID = 'Internal ID',
  CHART_NUMBER = 'Chart Number',
  PARTIAL_EMAIL = 'Partial Email',
}

export enum AMDFieldLabels {
  ACCOUNT_NUMBER = 'AMD Account #',
  INTERNAL_ID = 'AMD Internal ID',
  CHART_NUMBER = 'AMD Chart #',
  PARTIAL_EMAIL = 'Partial email',
}
